import React, { memo, useMemo, useRef } from 'react';
import buildClassName from 'js/utils/classNameBuilder.util';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useCommonTableWidthObserver } from 'js/hooks/useCommonTableWidthObserver';

import * as hooks from './hooks';
import { TableBody } from './components/TableBody';
import { TableHeader } from './components/TableHeader';
import { TABLE_VIEW, DEFAULT_ACCORDION_VIEW_WIDTH_THRESHOLD } from './constants';

import style from './Table.scss';

/**
 * @typedef {"AUTO" | "TABLE" | "ACCORDION"} TableView
 */

/**
 * @typedef {Object} Filter - props copy common Select props
 * @property {string | number} id
 * @property {string} name
 * @property {boolean} [disabled]
 * @property {Array<{label: string, value: string | number}>} options
 * @property {Function} onChange - filters form values are embedded into params (event, values) => {...}
 * @property {string} label
 * @property {string} [placeholder]
 * @property {string} [hint]
 */

/**
 * @typedef {Object} Pagination
 * @property {number} max
 * @property {number} totalRecords
 * @property {Function} [setPage]
 * @property {Function} [setMaxPage]
 * @property {boolean} [showCurrentRage=true]
 * @property {boolean} [showPerPage=true]
 * @property {Array} [paginationOptions=[]]
 * @property {*} [componentRef] React compatible component
 */

/**
 * @typedef {Object} ClassNames
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [tableContainer]
 * @property {string} [error]
 * @property {string} [tableHeaderContainer]
 * @property {string} [filtersContainer]
 * @property {string} [filters]
 * @property {string} [filter]
 * @property {string} [anotherPeriodContainer]
 * @property {string} [anotherPeriodSubmit]
 * @property {string} [afterFiltersControlsContainer]
 * @property {string} [loadMoreContainer]
 * @property {string} [loadMoreBtn]
 * @property {string} [tableBodyContainer]
 * @property {string} [tableBodyInfo]
 * @property {string} [tableBodyEmpty]
 * @property {string} [tableHeaderRowContainer]
 * @property {string} [tableHeaderRowColumn]
 * @property {string} [tableBodyRowContainer]
 * @property {string} [tableBodyRowColumn]
 * @property {string} [tableDataContainer]
 * @property {string} [accordionContainer]
 * @property {string} [accordionHeaderContainer]
 * @property {string} [accordionHeaderCell]
 * @property {string} [accordionHeaderArrowRow]
 * @property {string} [accordionBodyContainer]
 * @property {string} [accordionBodyCell]
 */

/**
 * @typedef {Object} TableCell
 * @property {string | number} id
 * @property {any} [content] React compatible component
 * @property {string} [className]
 */

/**
 * @typedef {Object} TableRow
 * @property {string} id
 * @property {TableCell[]} [rowCells] React compatible component
 */

/**
 * @typedef {Object} AccordionViewRow
 * @property {string} id
 * @property {boolean} [isExpandedByDefault]
 * @property {TableCell[]} [headerCells]
 * @property {TableCell[]} [rowCells]
 */

/**
 * @typedef {Object} TableSummary
 * @property {string | number} id
 * @property {boolean} [isExpandedByDefault]
 *
 * @property {Object} [tableView]
 * @property {TableCell[]} tableView.headerCells
 * @property {TableCell[]} tableView.rowCells
 *
 * @property {Object} accordionView
 * @property {TableCell[]} accordionView.headerCells
 * @property {TableCell[]} accordionView.rowCells
 * @property {TableCell[]} [accordionView.tableViewHeaderCells]
 */

/**
 * @typedef {Object} TableData
 *
 * @property {Object} [tableView]
 * @property {TableCell[]} [tableView.headerCells]
 * @property {TableRow[]} [tableView.rows]
 *
 * @property {Object} [accordionView]
 * @property {AccordionViewRow[]} accordionView.rows
 * @property {TableCell[]} [accordionView.tableViewHeaderCells]
 */

/**
 * @param props {Object}
 * @param [props.className] {String} Class added to tableContainer
 * @param [props.title] {string}
 * @param [props.error] {string}
 * @param [props.filters] {Filter[]}
 * @param [props.loadMore] {{onClick: Function, disabled: boolean, title: string} | null}
 * @param [props.tableData] {TableData}
 * @param [props.pagination] {Pagination}
 * @param [props.emptyTitle] {string}
 * @param [props.description] {string}
 * @param [props.tableSummary] {TableSummary[] | null}
 * @param [props.headerValidate] {Function}
 * @param [props.onAccordionExpand] {Function}
 * @param [props.classNames] {ClassNames}
 * @param [props.isLoading=false] {boolean}
 * @param [props.anotherPeriodFilter] {{from: Object, until: Object, submit: {title: string, getIsDisabled: Function}}
 * @param [props.headerInitialValues] {Object} - initial values for filters form
 * @param [props.onAnotherPeriodApply] {Function} - will work if anotherPeriodFilter was passed
 * @param [props.showTableBody=true] {boolean} - specific property to hide table body
 * @param [props.afterFiltersControls] {Array<{id: string, content: any}>} - content should be React compatible component
 * @param [props.beforeTableDataContent] {any} - React compatible component, content between table data and summary
 * @param [props.headerValidationConfig] {Object} - validation config for filters form
 * @param [props.tableRowView="AUTO"] {TableView} - table row view (if set "AUTO" then "ACCORDION" view will be applied for mobile and tablet and "TABLE" view will be applied for desktop if table width <640px, "ACCORDION" view will be applied)
 * @param [props.summaryRowView="AUTO"] {TableView} - summary row view (if set "AUTO" then "ACCORDION" view will be applied for mobile and tablet and "TABLE" view will be applied for desktop if table width <640px, "ACCORDION" view will be applied)
 * @param [props.accordionViewWidthThreshold=640] {number} - if table width less than it, "ACCORDION" view will be applied
 */

export const TableBare = (props) => {
    const {
        title,
        error,
        filters,
        loadMore,
        tableData,
        pagination,
        emptyTitle,
        description,
        tableSummary,
        headerValidate,
        isLoading = false,
        onAccordionExpand,
        onAccordionCollapse,
        anotherPeriodFilter,
        headerInitialValues,
        onAnotherPeriodApply,
        showTableBody = true,
        afterFiltersControls,
        beforeTableDataContent,
        headerValidationConfig,
        tableRef: propsTableRef,
        classNames: propsClassNames,
        className = '',
        tableRowView = TABLE_VIEW.AUTO,
        summaryRowView = TABLE_VIEW.AUTO,
        accordionViewWidthThreshold = DEFAULT_ACCORDION_VIEW_WIDTH_THRESHOLD
    } = props;
    const containerRef = useRef();
    const tableRef = propsTableRef || containerRef;
    const isForcedAccordionView = useCommonTableWidthObserver(tableRef, accordionViewWidthThreshold);
    const classNames = useMemo(() => propsClassNames || {}, [propsClassNames]);
    const actualTableRowView = hooks.useActualTableView(tableRowView, isForcedAccordionView);
    const actualSummaryRowView = hooks.useActualTableView(summaryRowView, isForcedAccordionView);

    return (
        <div
            ref={tableRef}
            className={`${buildClassName(['tableContainer'], style, classNames)} ${isForcedAccordionView ? 'mobileView' : ''} cms-table ${className} cms-table-tableview-${actualTableRowView} cms-table-summaryview-${actualSummaryRowView}`}
        >
            {title && <h2 className={buildClassName(['title'], style, classNames)}>{title}</h2>}
            {description && <p className={buildClassName(['description'], style, classNames)}>{description}</p>}
            {error && (
                <ErrorMessage
                    error={error}
                    className={buildClassName(['error'], style, classNames)}
                />
            )}
            {(filters || afterFiltersControls || anotherPeriodFilter) && (
                <TableHeader
                    filters={filters}
                    classNames={classNames}
                    validate={headerValidate}
                    afterFiltersControls={afterFiltersControls}
                    onAnotherPeriodApply={onAnotherPeriodApply}
                    {...anotherPeriodFilter && { anotherPeriodFilter }}
                    {...headerInitialValues && { initialValues: headerInitialValues }}
                    {...headerValidationConfig && { validationConfig: headerValidationConfig }}
                />
            )}
            {showTableBody && (
                <TableBody
                    isLoading={isLoading}
                    emptyTitle={emptyTitle}
                    classNames={classNames}
                    tableRowView={actualTableRowView}
                    summaryRowView={actualSummaryRowView}
                    beforeTableDataContent={beforeTableDataContent}
                    {...loadMore && { loadMore }}
                    {...tableData && { tableData }}
                    {...pagination && { pagination }}
                    {...tableSummary && { tableSummary }}
                    {...onAccordionExpand && { onAccordionExpand }}
                    {...onAccordionCollapse && { onAccordionCollapse }}
                />
            )}
        </div>
    );
};

export const Table = memo(TableBare);
